import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';

const SettingFilePasswords = ({ caseid }) => {
  const { t } = useTranslation();
  const { ws } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [entries, setEntries] = useState([]);
  const [savedEntries, setSavedEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const nextId = useRef(1);

  const withEntryIds = (list) => (list || []).map((entry) => {
    if (entry && typeof entry === 'object' && entry.id !== undefined && entry.id !== null) {
      return entry;
    }
    const id = nextId.current;
    nextId.current += 1;
    return { ...entry, id };
  });
  const normalizeEntries = (list) => (list || []).reduce((acc, entry) => {
    if (!entry || typeof entry !== 'object') {
      return acc;
    }
    const rawExtension = entry.extension === undefined || entry.extension === null ? '' : String(entry.extension);
    const rawPassword = entry.password === undefined || entry.password === null ? '' : String(entry.password);
    let extension = rawExtension.trim().toLowerCase();
    if (!extension) {
      return acc;
    }
    if (!extension.startsWith('.')) {
      extension = `.${extension}`;
    }
    if (!rawPassword) {
      return acc;
    }
    acc.push({ extension, password: rawPassword });
    return acc;
  }, []);
  const normalizedEntries = normalizeEntries(entries);
  const hasChanges = JSON.stringify(normalizedEntries) !== JSON.stringify(savedEntries);
  const saveTooltip = hasChanges
    ? t('settings.file_password.save_tooltip')
    : t('settings.file_password.save_disabled_tooltip');

  useEffect(() => {
    if (ws && caseid) {
      setLoading(true);
      ws.send(JSON.stringify({ command: 'getFilePasswords', caseid }));
    } else {
      setLoading(false);
    }
  }, [ws, caseid]);

  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const payload = message.data || {};

        if (payload.caseid && caseid && String(payload.caseid) !== String(caseid)) {
          return;
        }

        if (message.command === 'getFilePasswords') {
          if (payload.status === 'success') {
            setEntries(withEntryIds(payload.file_passwords || []));
            setSavedEntries(normalizeEntries(payload.file_passwords || []));
          } else {
            setSnackbar({
              open: true,
              message: payload.message || t('settings.file_password.load_error'),
              severity: 'error'
            });
          }
          setLoading(false);
        } else if (message.command === 'updateFilePasswords') {
          setSaving(false);
          if (payload.status === 'success') {
            setEntries(withEntryIds(payload.file_passwords || []));
            setSavedEntries(normalizeEntries(payload.file_passwords || []));
            setSnackbar({
              open: true,
              message: t('settings.file_password.save_success'),
              severity: 'success'
            });
          } else {
            setSnackbar({
              open: true,
              message: payload.message || t('settings.file_password.save_error'),
              severity: 'error'
            });
          }
        }
      } catch (error) {
        console.error('Error parsing file password message:', error);
      }
    };

    ws.addEventListener('message', handleMessage);
    return () => ws.removeEventListener('message', handleMessage);
  }, [ws, caseid, t]);

  const handleEntryChange = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const handleAddEntry = () => {
    const id = nextId.current;
    nextId.current += 1;
    setEntries((prev) => [...prev, { id, extension: '', password: '' }]);
  };

  const handleRemoveEntry = (id) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleSave = () => {
    if (!ws) {
      setSnackbar({
        open: true,
        message: t('settings.file_password.load_error'),
        severity: 'error'
      });
      return;
    }

    const hasPartial = entries.some((entry) => {
      const extension = (entry.extension || '').trim();
      const password = (entry.password || '').trim();
      return (extension && !password) || (!extension && password);
    });

    if (hasPartial) {
      setSnackbar({
        open: true,
        message: t('settings.file_password.validation_error'),
        severity: 'error'
      });
      return;
    }

    const sanitizedEntries = normalizeEntries(entries);

    setSaving(true);
    ws.send(JSON.stringify({
      command: 'updateFilePasswords',
      caseid,
      file_passwords: sanitizedEntries
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 }, backgroundColor: 'transparent' }}>
      <Box sx={{ maxWidth: 720, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: { xs: 1, sm: 0 } }}>
          <Typography variant={isMobile ? "h6" : "h5"}>
            {t('settings.file_password.title', 'File Password')}
          </Typography>
          <Tooltip title={t('settings.file_password.description')}>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Alert severity="info" sx={{ borderRadius: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {t('settings.file_password.hint')}
        </Alert>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#5757b0' }} />
          </Box>
        ) : entries.length === 0 ? (
          <Box
            sx={{
              borderRadius: 2,
              border: '1px dashed rgba(87, 87, 176, 0.3)',
              p: { xs: 2, sm: 3 },
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('settings.file_password.empty')}
            </Typography>
            <Button
              variant="outlined"
              fullWidth={isMobile}
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              sx={{ borderColor: '#5757b0', color: '#5757b0' }}
            >
              {t('settings.file_password.add_entry')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {entries.map((entry, index) => (
              <Box
                key={entry.id ?? index}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 1.5, sm: 2 },
                  alignItems: 'center',
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  border: '1px solid rgba(87, 87, 176, 0.15)',
                  backgroundColor: 'rgba(87, 87, 176, 0.04)'
                }}
              >
                <TextField
                  label={t('settings.file_password.extension')}
                  value={entry.extension || ''}
                  onChange={(event) => handleEntryChange(entry.id, 'extension', event.target.value)}
                  placeholder=".docx"
                  size="small"
                  sx={{ flex: '1 1 100%', sm: { flex: '1 1 180px' } }}
                />
                <TextField
                  label={t('settings.file_password.password')}
                  type="password"
                  value={entry.password || ''}
                  onChange={(event) => handleEntryChange(entry.id, 'password', event.target.value)}
                  placeholder={t('settings.file_password.password')}
                  size="small"
                  sx={{ flex: '1 1 100%', sm: { flex: '1 1 220px' } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: { xs: '100%', sm: 'auto' } }}>
                  <IconButton
                    aria-label={t('settings.file_password.remove_entry', 'Remove')}
                    onClick={() => handleRemoveEntry(entry.id)}
                    sx={{ color: '#ff6666' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            startIcon={<AddIcon />}
            onClick={handleAddEntry}
            sx={{ borderColor: '#5757b0', color: '#5757b0' }}
          >
            {t('settings.file_password.add_entry')}
          </Button>
          <Tooltip title={saveTooltip}>
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                fullWidth={isMobile}
                startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || loading || !hasChanges}
                sx={{
                  backgroundColor: '#5757b0',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#aaaaca' }
                }}
              >
                {t('settings.file_password.save')}
              </Button>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SettingFilePasswords;
